import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import APRComment from "./APRComment.js/AprComments";
import APRmemo from "./APRComment.js/APRmemo";
import axios from "../../api/axios";
import { getAttributeValue, getProjectDetails, groupProjectByDevelopmentDimension } from "../../utils/utils";

const PROJECT_PROGRAM = "g3wMUKEMmH3";
const PROJECT_TYPE_ATTRIBUTE = "wtf3BR2YO8w";
const PROJECT_TYPE_VALUE = "Project";

function stripTotalRows(rows) {
  return rows.filter((row) => row.department !== "Total");
}

// Register chart components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const Table_4 = forwardRef(({ year, district, period,   hideTableDis }, ref) => { // Default year set to 2024, adjustable via prop
  const [tableData, setTableData] = useState([]);
  const [tableDataDummy, setTableDataDummy] = useState([]);

  useEffect(() => {
    getData();
  }, [district, year, period]);

   useImperativeHandle(ref, () => ({
      getData: () => ({
        indicator: "Table_4",
        tableData
      }),
    }));


  function getData() {
    const startDate = `${year}-01-01`;
    const endDate = `${year}-12-31`;

    axios
      .get(`/tracker/trackedEntities?orgUnit=${district}&program=${PROJECT_PROGRAM}&filter=${PROJECT_TYPE_ATTRIBUTE}:EQ:${PROJECT_TYPE_VALUE}&startDate=${startDate}&endDate=${endDate}&pageSize=5000`)
      .then(result => {

        axios
          .get(`/tracker/events?program=${PROJECT_PROGRAM}&orgUnit=${district}&startDate=${startDate}&endDate=${endDate}&pageSize=5000`)
          .then(resp => {
            const reports = resp.data.instances;
            const instances = result.data.instances || [];
            const projectDetails = getProjectDetails(instances, reports).map((detail, idx) => ({
              ...detail,
              department: getAttributeValue("Development Dimension", instances[idx]) || detail.department,
            }));

            const developmentDimension = [
              "Economic Development (ED)", "Social Development (SD)",
              "Governance, Corruption And Public Accountability(GCPA)", "Environment, infrastructure and human settlement",
              "Emergency Planning And Covid-19 Response", "Implementation, Coordination, Monitoring And Evaluation"
            ];

            const groupDevelopment = stripTotalRows(groupProjectByDevelopmentDimension(projectDetails, developmentDimension, year));
            const groupDevelopment1 = stripTotalRows(groupProjectByDevelopmentDimension(projectDetails, developmentDimension, `${year - 1}`));
            const groupDevelopment2 = stripTotalRows(groupProjectByDevelopmentDimension(projectDetails, developmentDimension, `${year - 2}`));
            const dataGrouped = { groupDevelopment, groupDevelopment1, groupDevelopment2 };

            // Step 1: merge departments by index (assuming they align by index/department)
            const formatted = dataGrouped.groupDevelopment.map((dept, index) => {
              const dept1 = dataGrouped.groupDevelopment1[index];
              const dept2 = dataGrouped.groupDevelopment2[index];

              return {
                department: dept.department.replace(/\s*\([^)]*\)/, ""), // remove text in brackets e.g. (ED)
                rollover2: dept2.rollover,
                rollover1: dept1.rollover,
                rollover: dept.rollover,
                new2: dept2.new,
                new1: dept1.new,
                new: dept.new
              };
            });

            // Step 1: add totalRow for each department
            const withRowTotals = formatted.map(item => {
              const totalRow = item.rollover2 + item.rollover1 + item.rollover + item.new2 + item.new1 + item.new;
              return { ...item, totalRow };
            });

            // Step 2: compute overall totals row
            const totals = withRowTotals.reduce(
              (acc, item) => {
                acc.rollover2 += item.rollover2;
                acc.rollover1 += item.rollover1;
                acc.rollover += item.rollover;
                acc.new2 += item.new2;
                acc.new1 += item.new1;
                acc.new += item.new;
                acc.totalRow += item.totalRow;
                return acc;
              },
              { department: "Total", rollover2: 0, rollover1: 0, rollover: 0, new2: 0, new1: 0, new: 0, totalRow: 0 }
            );

            // Step 3: append total row
            const finalResult = [...withRowTotals, totals];


            setTableDataDummy(finalResult);
            setTableData(finalResult)


          })
          .catch(err => console.log(err))


      })
      .catch(err => console.log(err))
  };


  const chartRows = tableData.filter((row) => row.department !== "Total");
  const selectedYear = parseInt(year, 10) || new Date().getFullYear();

  const chartData = {
    labels: chartRows.map((row) => row.department),
    datasets: [
      {
        label: `Rollover ${selectedYear - 2}`,
        data: chartRows.map((row) => row.rollover2),
        backgroundColor: "#e65100",
        borderColor: "#bf360c",
        borderWidth: 1,
      },
      {
        label: `Rollover ${selectedYear - 1}`,
        data: chartRows.map((row) => row.rollover1),
        backgroundColor: "#ff9800",
        borderColor: "#e07a00",
        borderWidth: 1,
      },
      {
        label: `Rollover ${selectedYear}`,
        data: chartRows.map((row) => row.rollover),
        backgroundColor: "#ffb74d",
        borderColor: "#f57c00",
        borderWidth: 1,
      },
      {
        label: `New ${selectedYear - 2}`,
        data: chartRows.map((row) => row.new2),
        backgroundColor: "#1b5e20",
        borderColor: "#0d3d13",
        borderWidth: 1,
      },
      {
        label: `New ${selectedYear - 1}`,
        data: chartRows.map((row) => row.new1),
        backgroundColor: "#28a745",
        borderColor: "#1e7e34",
        borderWidth: 1,
      },
      {
        label: `New ${selectedYear}`,
        data: chartRows.map((row) => row.new),
        backgroundColor: "#81c784",
        borderColor: "#43a047",
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: `Rollover and New Projects by Development Dimension (${selectedYear - 2}–${selectedYear})`,
      },
    },
    scales: {
      x: {
        ticks: {
          maxRotation: 45,
          minRotation: 0,
        },
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Number of Projects",
        },
      },
    },
  };

  return (
    <div className="col-12">
      <h3>Table 4: Total number of active projects</h3>
      <div className="card">
        <div className="card-header"></div>
        <div className="card-body">
          <APRmemo
            year={year}
            districtId={district}
            tableCommentedId={`table4-${year}`}
              hideTableDis={hideTableDis}

          />
          <div className="table-responsive">
            <table className="table table-bordered">
              <thead style={{ backgroundColor: '#d4edda', fontWeight: 'bold', border: '1px solid #000' }}>
                <tr>
                  <th style={{ border: '1px solid #000', fontWeight: 'bold' }}>Development Dimension</th>
                  <th colSpan="3" style={{ border: '1px solid #000', fontWeight: 'bold' }}>Roll over projects from previous years</th>
                  <th colSpan="3" style={{ border: '1px solid #000', fontWeight: 'bold' }}>Approved new projects introduced in the year</th>
                  <th style={{ border: '1px solid #000', fontWeight: 'bold' }}>Total</th>
                </tr>

                <tr>
                  <th style={{ border: '1px solid #000', fontWeight: 'bold' }}></th>
                  <th style={{ border: '1px solid #000', fontWeight: 'bold' }}>{year - 2}</th>
                  <th style={{ border: '1px solid #000', fontWeight: 'bold' }}>{year - 1}</th>
                  <th style={{ border: '1px solid #000', fontWeight: 'bold' }}>{year}</th>
                  <th style={{ border: '1px solid #000', fontWeight: 'bold' }}>{year - 2}</th>
                  <th style={{ border: '1px solid #000', fontWeight: 'bold' }}>{year - 1}</th>
                  <th style={{ border: '1px solid #000', fontWeight: 'bold' }}>{year}</th>

                  <th style={{ border: '1px solid #000', fontWeight: 'bold' }}></th>
                </tr>
              </thead>
              <tbody>
                {tableData.map((row, index) => (
                  <tr key={index}>
                    <td style={{ border: '1px solid #000' }}>{row.department}</td>
                    <td style={{ border: '1px solid #000' }}>{row.rollover2}</td>
                    <td style={{ border: '1px solid #000' }}>{row.rollover1}</td>
                    <td style={{ border: '1px solid #000' }}>{row.rollover}</td>
                    <td style={{ border: '1px solid #000' }}>{row.new2}</td>
                    <td style={{ border: '1px solid #000' }}>{row.new1}</td>
                    <td style={{ border: '1px solid #000' }}>{row.new}</td>
                    <td style={{ border: '1px solid #000' }}>{row.totalRow}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <APRComment
            data={tableData}
            year={year}
            districtId={district}
            tableCommentedId={`table4-${year}`}
              hideTableDis={hideTableDis}

          >
            {({ renderCommentInput, renderCommentList }) => (
              <>
                {renderCommentInput()}
                {renderCommentList()}
              </>
            )}
          </APRComment>

          {/* Bar Chart */}
          <h4>Figure 4: Rollover and New Projects by Development Dimension</h4>
          <Bar data={chartData} options={chartOptions} />
        </div>
      </div>
    </div>
  );
});

export default Table_4;